// Per-service content for the shared ServiceHero layout.
// Copy is intentionally concise — descriptions must stay within two lines,
// and the component also enforces this with line-clamp as a hard guarantee.
import {
  TrendingUp,
  DollarSign,
  BadgeCheck,
  Users,
  Clock,
  Star,
  Settings,
  ClipboardList,
  BarChart3,
  Sparkles,
  Truck,
  PartyPopper,
  Megaphone,
  Globe,
  HeartHandshake,
  Wallet,
  GraduationCap,
  Building2,
  Package,
  ChefHat,
  Search,
  Repeat,
  ShieldCheck,
  Smile,
} from "lucide-react";

const serviceHeroData = {
  "process-optimization": {
    icon: Settings,
    description:
      "We analyze, refine, and optimize your business processes to eliminate waste, reduce costs, and improve performance.",
    benefits: [
      { icon: TrendingUp, label: "Higher Efficiency" },
      { icon: DollarSign, label: "Lower Costs" },
      { icon: BadgeCheck, label: "Better Quality" },
      { icon: Users, label: "Happier Customers" },
    ],
    cardHeadline: "Build Smarter. Operate Better. Grow Faster.",
    cardText:
      "We turn reactive operations into systematic excellence — eliminating bottlenecks and locking in consistency across every shift and location.",
    stats: [
      { icon: Clock, value: "30%+", label: "Time Saved" },
      { icon: DollarSign, value: "20%+", label: "Cost Reduction" },
      { icon: Star, value: "98%+", label: "Quality Consistency" },
    ],
    ctaLabel: "Optimize Your Operations Today",
  },
  "menu-engineering": {
    icon: ClipboardList,
    description:
      "We blend culinary art with business science to turn your menu from a price list into your most powerful sales asset.",
    benefits: [
      { icon: DollarSign, label: "Higher Margins" },
      { icon: Star, label: "Star Dishes" },
      { icon: BarChart3, label: "Data-Driven Pricing" },
      { icon: Smile, label: "Delighted Guests" },
    ],
    cardHeadline: "Design Menus. Drive Profit. Delight Guests.",
    cardText:
      "A well-engineered menu can lift profits 10–15% without a single new customer — we analyze every dish to make each cover count.",
    stats: [
      { icon: TrendingUp, value: "10-15%", label: "Profit Uplift" },
      { icon: DollarSign, value: "25-35%", label: "Ideal Food Cost" },
      { icon: Star, value: "100%", label: "Menu Coverage" },
    ],
    ctaLabel: "Engineer Your Menu Today",
  },
  "kitchen-operations": {
    icon: ChefHat,
    description:
      "We replace kitchen chaos with choreography — tighter prep, faster tickets, and consistent quality at every station.",
    benefits: [
      { icon: Clock, label: "Faster Tickets" },
      { icon: BadgeCheck, label: "Consistent Quality" },
      { icon: TrendingUp, label: "Smoother Service" },
      { icon: Users, label: "Confident Teams" },
    ],
    cardHeadline: "Prep Smarter. Cook Faster. Serve Consistently.",
    cardText:
      "From station layouts to line checks, we build kitchen systems that hold up under pressure — every shift, every location.",
    stats: [
      { icon: Clock, value: "40%+", label: "Faster Prep" },
      { icon: Star, value: "98%+", label: "Order Accuracy" },
      { icon: TrendingUp, value: "60%+", label: "Fewer Errors" },
    ],
    ctaLabel: "Transform Your Kitchen Today",
  },
  "inventory-management": {
    icon: Package,
    description:
      "We turn your storage room into a bank vault — precise stock control that stops shrinkage and protects your margins.",
    benefits: [
      { icon: DollarSign, label: "Less Waste" },
      { icon: BarChart3, label: "Accurate Counts" },
      { icon: Clock, label: "Faster Ordering" },
      { icon: ShieldCheck, label: "Zero Surprises" },
    ],
    cardHeadline: "Count Less. Control More. Waste Nothing.",
    cardText:
      "Smart par levels, supplier discipline, and real-time tracking that plug profit leaks before they drain your bottom line.",
    stats: [
      { icon: DollarSign, value: "30%+", label: "Waste Reduction" },
      { icon: BarChart3, value: "99%", label: "Stock Accuracy" },
      { icon: Clock, value: "50%", label: "Faster Counts" },
    ],
    ctaLabel: "Take Control of Your Stock",
  },
  "branding-marketing": {
    icon: Megaphone,
    description:
      "We make your restaurant magnetic — a brand and marketing engine that fills seats and keeps guests coming back.",
    benefits: [
      { icon: Sparkles, label: "Memorable Brand" },
      { icon: TrendingUp, label: "More Footfall" },
      { icon: Users, label: "Loyal Community" },
      { icon: BarChart3, label: "Measurable ROI" },
    ],
    cardHeadline: "Stand Out. Get Found. Stay Remembered.",
    cardText:
      "From identity to campaigns, we build marketing systems that turn first-time visitors into regulars — measured, not guessed.",
    stats: [
      { icon: TrendingUp, value: "2x", label: "Brand Visibility" },
      { icon: Users, value: "40%+", label: "Repeat Guests" },
      { icon: BarChart3, value: "100%", label: "Tracked Campaigns" },
    ],
    ctaLabel: "Build Your Brand Today",
  },
  "digital-profiling": {
    icon: Globe,
    description:
      "We build your digital trust — polished profiles and reviews that make you the obvious choice in local search.",
    benefits: [
      { icon: Search, label: "Found First" },
      { icon: Star, label: "Stronger Reviews" },
      { icon: Globe, label: "Polished Profiles" },
      { icon: TrendingUp, label: "More Enquiries" },
    ],
    cardHeadline: "Get Found. Look Great. Win Trust.",
    cardText:
      "Google, Maps, and review platforms — optimized end to end so your online presence sells before a guest ever walks in.",
    stats: [
      { icon: Search, value: "3x", label: "Search Visibility" },
      { icon: Star, value: "4.5+", label: "Rating Targets" },
      { icon: TrendingUp, value: "50%+", label: "More Enquiries" },
    ],
    ctaLabel: "Own Your Online Presence",
  },
  "delivery-platforms": {
    icon: Truck,
    description:
      "We turn delivery from a margin-killer into a profit engine — optimized listings, ratings, and platform economics.",
    benefits: [
      { icon: TrendingUp, label: "Higher Rankings" },
      { icon: DollarSign, label: "Better Margins" },
      { icon: Star, label: "Stronger Ratings" },
      { icon: Repeat, label: "Repeat Orders" },
    ],
    cardHeadline: "Rank Higher. Sell More. Keep Margins.",
    cardText:
      "Menu, pricing, packaging, and promotions tuned for the platforms — so every delivery order actually makes you money.",
    stats: [
      { icon: TrendingUp, value: "2x", label: "Order Volume" },
      { icon: Star, value: "4.3+", label: "Platform Rating" },
      { icon: DollarSign, value: "15%+", label: "Margin Recovery" },
    ],
    ctaLabel: "Master Your Delivery Channels",
  },
  "catering-events": {
    icon: PartyPopper,
    description:
      "We unlock revenue without walls — catering and event systems that scale your kitchen far beyond your dining room.",
    benefits: [
      { icon: DollarSign, label: "New Revenue" },
      { icon: ClipboardList, label: "Flawless Execution" },
      { icon: Users, label: "Bigger Audiences" },
      { icon: Repeat, label: "Repeat Bookings" },
    ],
    cardHeadline: "Plan Better. Execute Flawlessly. Earn More.",
    cardText:
      "From quoting to logistics, we systemize your catering arm so every event runs on rails — and every booking is profitable.",
    stats: [
      { icon: DollarSign, value: "25%+", label: "Revenue Growth" },
      { icon: BadgeCheck, value: "100%", label: "Event Checklists" },
      { icon: Repeat, value: "60%+", label: "Rebooking Rate" },
    ],
    ctaLabel: "Grow Beyond Your Four Walls",
  },
  "crm": {
    icon: HeartHandshake,
    description:
      "We help you own your audience — guest data and loyalty systems that turn one-time visitors into lifelong regulars.",
    benefits: [
      { icon: Users, label: "Known Guests" },
      { icon: Repeat, label: "More Visits" },
      { icon: Sparkles, label: "Personal Touch" },
      { icon: TrendingUp, label: "Higher Spend" },
    ],
    cardHeadline: "Know Your Guests. Win Their Loyalty.",
    cardText:
      "Capture guest data, automate follow-ups, and reward loyalty — a retention engine that grows revenue you already earned.",
    stats: [
      { icon: Repeat, value: "40%+", label: "Repeat Visits" },
      { icon: Users, value: "100%", label: "Guest Profiles" },
      { icon: TrendingUp, value: "20%+", label: "Higher Spend" },
    ],
    ctaLabel: "Build Guest Loyalty Today",
  },
  "financial-accounts": {
    icon: Wallet,
    description:
      "We give you a live business dashboard — clean books and clear numbers that power confident, profitable decisions.",
    benefits: [
      { icon: BarChart3, label: "Clear Numbers" },
      { icon: DollarSign, label: "Protected Margins" },
      { icon: ShieldCheck, label: "Full Compliance" },
      { icon: TrendingUp, label: "Smarter Decisions" },
    ],
    cardHeadline: "See Clearly. Decide Confidently. Grow Profitably.",
    cardText:
      "P&L discipline, cost tracking, and reporting rhythms that show exactly where money is made — and where it leaks.",
    stats: [
      { icon: BarChart3, value: "100%", label: "Financial Visibility" },
      { icon: DollarSign, value: "15%+", label: "Cost Savings" },
      { icon: Clock, value: "Monthly", label: "Clear Reporting" },
    ],
    ctaLabel: "Get Financial Clarity Today",
  },
  "employee-management": {
    icon: GraduationCap,
    description:
      "We transform staff into your competitive advantage — hiring, training, and retention systems that actually stick.",
    benefits: [
      { icon: GraduationCap, label: "Faster Onboarding" },
      { icon: Users, label: "Lower Turnover" },
      { icon: BadgeCheck, label: "Consistent Standards" },
      { icon: Smile, label: "Motivated Teams" },
    ],
    cardHeadline: "Hire Well. Train Fast. Retain Longer.",
    cardText:
      "Structured onboarding and development frameworks that cut training time by 40% and keep your best people on the team.",
    stats: [
      { icon: Clock, value: "40%", label: "Faster Onboarding" },
      { icon: Users, value: "30%+", label: "Lower Turnover" },
      { icon: Star, value: "100%", label: "Role Clarity" },
    ],
    ctaLabel: "Build Your Dream Team",
  },
  "infrastructure-management": {
    icon: Building2,
    description:
      "We make your infrastructure invisible — proactive maintenance that keeps equipment running and service uninterrupted.",
    benefits: [
      { icon: ShieldCheck, label: "Fewer Breakdowns" },
      { icon: Clock, label: "Zero Downtime" },
      { icon: DollarSign, label: "Lower Repair Costs" },
      { icon: BadgeCheck, label: "Safe & Compliant" },
    ],
    cardHeadline: "Prevent Problems. Protect Uptime. Preserve Value.",
    cardText:
      "Preventive schedules, vendor management, and asset tracking that catch issues before they ever interrupt a service.",
    stats: [
      { icon: ShieldCheck, value: "70%+", label: "Fewer Breakdowns" },
      { icon: DollarSign, value: "25%+", label: "Maintenance Savings" },
      { icon: Clock, value: "99%", label: "Equipment Uptime" },
    ],
    ctaLabel: "Protect Your Investment Today",
  },
};

export default serviceHeroData;
